import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  AppBar,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  Speed as PerformanceIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Memory as MemoryIcon,
  Storage as StorageIcon,
  Hub as NetworkIcon,
  Timer as TimerIcon,
  Security as SecurityIcon,
  Analytics as AnalyticsIcon,
  Error as ErrorIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { getFromStorage } from '../utils/storage';

const PerformanceAvo = () => {
  // State management
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [performanceData, setPerformanceData] = useState(null);
  const [networkStats, setNetworkStats] = useState(null);
  const [validatorStats, setValidatorStats] = useState(null);
  const [systemHealth, setSystemHealth] = useState(null);
  const [error, setError] = useState('');

  // Initialize component
  useEffect(() => {
    initializePerformance();
    const interval = setInterval(initializePerformance, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const initializePerformance = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Simulate performance data loading
      await loadPerformanceData();
      await loadNetworkStats();
      await loadValidatorStats();
      await loadSystemHealth();
      
    } catch (err) {
      console.error('Error loading performance data:', err);
      setError('Failed to load performance data. Some features may not be available.');
    } finally {
      setLoading(false);
    }
  };

  const loadPerformanceData = async () => {
    // Mock performance data - in real implementation would call RPC methods
    const mockData = {
      tps: Math.floor(Math.random() * 1000) + 500,
      block_time: (Math.random() * 2 + 4).toFixed(2),
      gas_price: (Math.random() * 0.1 + 0.05).toFixed(6),
      total_transactions: Math.floor(Math.random() * 100000) + 500000,
      network_utilization: Math.floor(Math.random() * 40) + 30,
      uptime: 99.8,
      last_block: Date.now() - Math.floor(Math.random() * 10000),
      peer_count: Math.floor(Math.random() * 50) + 100
    };
    setPerformanceData(mockData);
  };

  const loadNetworkStats = async () => {
    const mockStats = {
      chain_height: Math.floor(Math.random() * 10000) + 1000000,
      finalized_height: Math.floor(Math.random() * 10000) + 999990,
      pending_transactions: Math.floor(Math.random() * 100) + 50,
      memory_pool_size: Math.floor(Math.random() * 50) + 25,
      difficulty: '0x' + Math.floor(Math.random() * 1000000).toString(16),
      hash_rate: Math.floor(Math.random() * 1000) + 500
    };
    setNetworkStats(mockStats);
  };

  const loadValidatorStats = async () => {
    const mockValidatorStats = {
      active_validators: 15,
      total_validators: 18,
      missed_blocks: Math.floor(Math.random() * 5),
      validator_uptime: 99.2,
      consensus_participation: 98.7,
      last_consensus_round: Date.now() - Math.floor(Math.random() * 5000)
    };
    setValidatorStats(mockValidatorStats);
  };

  const loadSystemHealth = async () => {
    const mockHealth = {
      cpu_usage: Math.floor(Math.random() * 30) + 20,
      memory_usage: Math.floor(Math.random() * 40) + 30,
      disk_usage: Math.floor(Math.random() * 20) + 40,
      network_io: Math.floor(Math.random() * 100) + 50,
      status: 'healthy',
      alerts: [
        { type: 'info', message: 'System operating normally', timestamp: Date.now() - 300000 },
        { type: 'warning', message: 'High network activity detected', timestamp: Date.now() - 600000 }
      ]
    };
    setSystemHealth(mockHealth);
  };

  const formatTimestamp = (timestamp) => {
    const diff = Date.now() - timestamp;
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) return `${hours}h ${minutes % 60}m ago`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s ago`;
    return `${seconds}s ago`;
  };

  const getHealthStatus = (value, thresholds) => {
    if (value <= thresholds.good) return { color: 'success', icon: <CheckIcon /> };
    if (value <= thresholds.warning) return { color: 'warning', icon: <WarningIcon /> };
    return { color: 'error', icon: <ErrorIcon /> };
  };

  // Tab panels
  const TabPanel = ({ children, value, index }) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );

  if (loading && !performanceData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading Performance Metrics...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, margin: '0 auto', p: 2 }}>
      {/* Header */}
      <Paper elevation={3} sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center">
            <PerformanceIcon sx={{ fontSize: 40, color: 'white', mr: 2 }} />
            <Box>
              <Typography variant="h4" color="white" fontWeight="bold">
                📊 Performance Monitor
              </Typography>
              <Typography variant="subtitle1" color="white" opacity={0.9}>
                Real-time network performance and system health
              </Typography>
            </Box>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Chip
              icon={systemHealth?.status === 'healthy' ? <CheckIcon /> : <ErrorIcon />}
              label={systemHealth?.status === 'healthy' ? 'HEALTHY' : 'ISSUES'}
              color={systemHealth?.status === 'healthy' ? 'success' : 'error'}
              variant="filled"
              sx={{ color: 'white', fontWeight: 'bold' }}
            />
            <Tooltip title="Refresh metrics">
              <IconButton onClick={initializePerformance} sx={{ color: 'white' }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      {/* Alerts */}
      {error && (
        <Alert severity="warning" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Key Metrics Overview */}
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" mb={2}>🚀 Live Performance Metrics</Typography>
        <Grid container spacing={2}>
          <Grid item xs={6} md={3}>
            <Card elevation={1}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="primary">
                  {performanceData?.tps || 0}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  TPS
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card elevation={1}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="secondary">
                  {performanceData?.block_time || 0}s
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Block Time
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card elevation={1}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="success.main">
                  {performanceData?.uptime || 0}%
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Uptime
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card elevation={1}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="info.main">
                  {performanceData?.peer_count || 0}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Peers
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Navigation Tabs */}
      <Paper elevation={2} sx={{ mb: 3 }}>
        <AppBar position="static" color="transparent" elevation={0}>
          <Tabs 
            value={activeTab} 
            onChange={(e, newValue) => setActiveTab(newValue)}
            variant="fullWidth"
          >
            <Tab icon={<NetworkIcon />} label="Network" />
            <Tab icon={<SecurityIcon />} label="Validators" />
            <Tab icon={<MemoryIcon />} label="System" />
            <Tab icon={<AnalyticsIcon />} label="Analytics" />
          </Tabs>
        </AppBar>

        {/* Network Tab */}
        <TabPanel value={activeTab} index={0}>
          <Grid container spacing={3}>
            {/* Network Statistics */}
            <Grid item xs={12} md={6}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h6" color="primary" mb={2}>
                    🌐 Network Statistics
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText 
                        primary="Chain Height" 
                        secondary={networkStats?.chain_height?.toLocaleString() || 'Loading...'} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Finalized Height" 
                        secondary={networkStats?.finalized_height?.toLocaleString() || 'Loading...'} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Pending Transactions" 
                        secondary={networkStats?.pending_transactions || 'Loading...'} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Memory Pool Size" 
                        secondary={`${networkStats?.memory_pool_size || 0} MB`} 
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            {/* Performance Metrics */}
            <Grid item xs={12} md={6}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h6" color="secondary" mb={2}>
                    ⚡ Performance Metrics
                  </Typography>
                  <Box mb={2}>
                    <Typography variant="body2" color="textSecondary">
                      Network Utilization
                    </Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={performanceData?.network_utilization || 0} 
                      sx={{ mt: 1, height: 8, borderRadius: 4 }}
                    />
                    <Typography variant="caption">
                      {performanceData?.network_utilization || 0}%
                    </Typography>
                  </Box>
                  <List dense>
                    <ListItem>
                      <ListItemText 
                        primary="Average Gas Price" 
                        secondary={`${performanceData?.gas_price || 0} AVO`} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Total Transactions" 
                        secondary={performanceData?.total_transactions?.toLocaleString() || 'Loading...'} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Last Block" 
                        secondary={formatTimestamp(performanceData?.last_block || Date.now())} 
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Validators Tab */}
        <TabPanel value={activeTab} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h6" color="primary" mb={2}>
                    👑 Validator Network
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText 
                        primary="Active Validators" 
                        secondary={`${validatorStats?.active_validators || 0} / ${validatorStats?.total_validators || 0}`} 
                      />
                      <ListItemSecondaryAction>
                        <Chip 
                          label={validatorStats?.active_validators === validatorStats?.total_validators ? 'FULL' : 'PARTIAL'}
                          color={validatorStats?.active_validators === validatorStats?.total_validators ? 'success' : 'warning'}
                          size="small"
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Missed Blocks" 
                        secondary={validatorStats?.missed_blocks || 0} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Validator Uptime" 
                        secondary={`${validatorStats?.validator_uptime || 0}%`} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Consensus Participation" 
                        secondary={`${validatorStats?.consensus_participation || 0}%`} 
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h6" color="secondary" mb={2}>
                    🔄 Consensus Status
                  </Typography>
                  <Box mb={2}>
                    <Typography variant="body2" color="textSecondary">
                      Consensus Participation
                    </Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={validatorStats?.consensus_participation || 0} 
                      color="success"
                      sx={{ mt: 1, height: 8, borderRadius: 4 }}
                    />
                    <Typography variant="caption">
                      {validatorStats?.consensus_participation || 0}%
                    </Typography>
                  </Box>
                  <List dense>
                    <ListItem>
                      <ListItemText 
                        primary="Last Consensus Round" 
                        secondary={formatTimestamp(validatorStats?.last_consensus_round || Date.now())} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Network Security" 
                        secondary="High - 83% stake participation" 
                      />
                      <ListItemSecondaryAction>
                        <SecurityIcon color="success" />
                      </ListItemSecondaryAction>
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* System Tab */}
        <TabPanel value={activeTab} index={2}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h6" color="primary" mb={2}>
                    💻 System Resources
                  </Typography>
                  
                  {/* CPU Usage */}
                  <Box mb={2}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2">CPU Usage</Typography>
                      <Typography variant="body2" color="textSecondary">
                        {systemHealth?.cpu_usage || 0}%
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={systemHealth?.cpu_usage || 0} 
                      color={getHealthStatus(systemHealth?.cpu_usage || 0, {good: 50, warning: 80}).color}
                      sx={{ mt: 1, height: 6, borderRadius: 3 }}
                    />
                  </Box>

                  {/* Memory Usage */}
                  <Box mb={2}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2">Memory Usage</Typography>
                      <Typography variant="body2" color="textSecondary">
                        {systemHealth?.memory_usage || 0}%
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={systemHealth?.memory_usage || 0} 
                      color={getHealthStatus(systemHealth?.memory_usage || 0, {good: 60, warning: 85}).color}
                      sx={{ mt: 1, height: 6, borderRadius: 3 }}
                    />
                  </Box>

                  {/* Disk Usage */}
                  <Box mb={2}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2">Disk Usage</Typography>
                      <Typography variant="body2" color="textSecondary">
                        {systemHealth?.disk_usage || 0}%
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={systemHealth?.disk_usage || 0} 
                      color={getHealthStatus(systemHealth?.disk_usage || 0, {good: 70, warning: 90}).color}
                      sx={{ mt: 1, height: 6, borderRadius: 3 }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h6" color="secondary" mb={2}>
                    🚨 System Alerts
                  </Typography>
                  <List dense>
                    {systemHealth?.alerts?.map((alert, index) => (
                      <ListItem key={index}>
                        <ListItemText 
                          primary={alert.message}
                          secondary={formatTimestamp(alert.timestamp)}
                        />
                        <ListItemSecondaryAction>
                          {alert.type === 'error' && <ErrorIcon color="error" />}
                          {alert.type === 'warning' && <WarningIcon color="warning" />}
                          {alert.type === 'info' && <CheckIcon color="success" />}
                        </ListItemSecondaryAction>
                      </ListItem>
                    )) || (
                      <ListItem>
                        <ListItemText 
                          primary="No alerts"
                          secondary="System is operating normally"
                        />
                        <ListItemSecondaryAction>
                          <CheckIcon color="success" />
                        </ListItemSecondaryAction>
                      </ListItem>
                    )}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Analytics Tab */}
        <TabPanel value={activeTab} index={3}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h6" color="primary" mb={2}>
                    📈 Performance Analytics
                  </Typography>
                  <Alert severity="info">
                    <strong>Analytics Dashboard</strong><br/>
                    Historical performance charts and trends will be available here. 
                    This includes transaction throughput over time, block production rates, 
                    validator performance metrics, and network health trends.
                  </Alert>
                  
                  <Box mt={3}>
                    <Typography variant="subtitle1" mb={2}>
                      📊 Available Metrics:
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <List dense>
                          <ListItem>
                            <TrendingUpIcon color="primary" sx={{ mr: 1 }} />
                            <ListItemText primary="Transaction Throughput Trends" />
                          </ListItem>
                          <ListItem>
                            <TimerIcon color="secondary" sx={{ mr: 1 }} />
                            <ListItemText primary="Block Time Analysis" />
                          </ListItem>
                          <ListItem>
                            <NetworkIcon color="info" sx={{ mr: 1 }} />
                            <ListItemText primary="Network Utilization History" />
                          </ListItem>
                        </List>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <List dense>
                          <ListItem>
                            <SecurityIcon color="success" sx={{ mr: 1 }} />
                            <ListItemText primary="Validator Performance Tracking" />
                          </ListItem>
                          <ListItem>
                            <MemoryIcon color="warning" sx={{ mr: 1 }} />
                            <ListItemText primary="System Resource Monitoring" />
                          </ListItem>
                          <ListItem>
                            <AnalyticsIcon color="error" sx={{ mr: 1 }} />
                            <ListItemText primary="Consensus Health Metrics" />
                          </ListItem>
                        </List>
                      </Grid>
                    </Grid>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>
    </Box>
  );
};

export default PerformanceAvo;